/**
 * Render helpers for the side-by-side compare-sales table cells:
 * product / total / amount. Extracted from CompareSalesView so the
 * same cell shapes are reusable when other alignment surfaces
 * (Skimmer / Pooltrackr ↔ Xero) need to render the same column types.
 */
import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { formatCurrency } from '../../../utils/inspectorFormatters'
import type {
  LineItemLS,
  LineItemXero,
} from '../../../services/inspector/api'
import { COMPARE_COLORS, COMPARE_NUM_FONT_SX, renderDeltaChip } from './compareSalesPalette'

export const PRODUCT_FONT_SIZE = '0.8125rem'

// Inline badge styling shared between the NOTE and CREDIT indicators
// in renderProductCell. Kept local so the styling never drifts between
// the two badges.
const BADGE_SX = {
  display: 'inline-block',
  fontSize: '0.65rem',
  fontWeight: 600,
  px: 0.5,
  mr: 0.5,
  border: '1px solid',
  borderColor: 'text.disabled',
  borderRadius: 0.5,
  color: 'text.secondary',
  fontStyle: 'normal',
  verticalAlign: 'middle',
} as const

const roundToOneDp = (n: number): string => {
  const rounded = Math.round(n * 10) / 10
  return rounded.toString()
}

export const renderProductCell = (
  item: LineItemLS | LineItemXero | undefined,
  descriptionMismatch: boolean,
  red = false,
): React.ReactNode => {
  if (!item) {
    return <Typography variant="body2" color="text.disabled" sx={{ fontSize: PRODUCT_FONT_SIZE }}>-</Typography>
  }
  const text = 'product_name' in item ? item.product_name : item.description
  const isNote = 'is_synthetic_note' in item && item.is_synthetic_note
  // NOTE wins if both flags are set so a row gets at most one badge.
  // is_credit_workaround is set structurally on the LS side (negative
  // line on a sale whose Xero invoice has credit allocations) and
  // mirrored onto the matched Xero side in the popup backend.
  const isCredit =
    !isNote && 'is_credit_workaround' in item && item.is_credit_workaround
  return (
    <Typography
      variant="body2"
      sx={{
        fontSize: PRODUCT_FONT_SIZE,
        color: red
          ? COMPARE_COLORS.mismatchBorder
          : descriptionMismatch
            ? '#ed6c02'
            : undefined,
        fontWeight: red ? 600 : undefined,
        fontStyle: isNote || isCredit ? 'italic' : undefined,
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
      }}
    >
      {isNote && (
        <Box component="span" sx={BADGE_SX}>
          NOTE
        </Box>
      )}
      {isCredit && (
        <Box component="span" sx={BADGE_SX}>
          CREDIT
        </Box>
      )}
      {text}
    </Typography>
  )
}

export interface RenderTotalCellOptions {
  isCurrency: boolean
  mismatch: boolean
  align?: 'right' | 'center'
  /**
   * Suppress the grey diff chip below the value. Set on the LS side
   * of the compare table - LS is the source of truth, so showing the
   * delta on both sides is redundant. The red value colour still
   * renders on both sides to flag the mismatch.
   */
  hideDiff?: boolean
}

export const renderTotalCell = (
  value: number,
  diff: number,
  options: RenderTotalCellOptions,
): React.ReactNode => {
  const { isCurrency, mismatch, align = 'right', hideDiff = false } = options
  return (
    <Stack spacing={0.625} alignItems={align === 'center' ? 'center' : 'flex-end'}>
      <Typography
        sx={{
          // Shared Inter + tabular-nums stack so the compare view
          // reads as the same surface as the exceptions table.
          ...COMPARE_NUM_FONT_SX,
          fontWeight: 700,
          fontSize: '0.9rem',
          lineHeight: 1.2,
          color: mismatch ? COMPARE_COLORS.mismatchBorder : undefined,
        }}
      >
        {isCurrency ? formatCurrency(value) : roundToOneDp(value)}
      </Typography>
      {mismatch && !hideDiff && renderDeltaChip(diff, isCurrency)}
    </Stack>
  )
}

export const renderAmountCell = (
  value: number | undefined,
  red: boolean,
  isSynth: boolean,
  delta?: number | null,
): React.ReactNode => {
  if (value == null || isSynth) {
    return (
      <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'right' }}>
        -
      </Typography>
    )
  }
  // Optional delta chip stacks under the value on the Xero side, mirroring
  // the totals row treatment so a per-line price gap is visible without
  // cross-comparing cells. LS side passes delta=undefined and stays plain.
  const chip = delta != null ? renderDeltaChip(delta, true) : null
  return (
    <Stack spacing={0.375} alignItems="flex-end">
      <Typography
        variant="body2"
        sx={{
          ...COMPARE_NUM_FONT_SX,
          textAlign: 'right',
          color: red ? COMPARE_COLORS.mismatchBorder : undefined,
          fontWeight: red ? 600 : undefined,
        }}
      >
        {formatCurrency(value)}
      </Typography>
      {chip}
    </Stack>
  )
}
